import type { ChannelClient } from '@hibro/platform-core';
import type {
  AppAttention,
  InAppNotificationMessage,
  NotificationMessage,
} from '@hibro/shared';
import { ATTENTION_CHANGED_EVENT, NOTIFICATION_EVENT } from '@hibro/shared';
import { toast } from '@hibro/ui-client';
import { type ReactNode, useEffect } from 'react';

import { useT } from '../i18n/i18n';
import { playNotificationSound } from './notification-sound';

/**
 * Where in-app notifications arrive from, and where a click on one goes back
 * (见 23).
 *
 * Implemented by the app shell — the desktop from the preload IPC the main
 * process's notification driver sends on, the web from the SharedWorker port —
 * so `platform-renderer` stays free of both globals. Same arrangement as
 * `CommandRequestSource` and `NavigationSource`, and a runtime that provides
 * none simply never shows one.
 *
 * **The click reports a tag rather than navigating.** The destination lives with
 * the host's router, which is what makes an in-app toast and an OS notification
 * take the user to the same place (见 23 §2).
 */
export interface InAppNotificationBridge {
  /** Subscribe to notifications and dismissals; returns a disposer. */
  subscribe(listener: (message: InAppNotificationMessage) => void): () => void;
  /** Report that the user clicked the notification carrying `tag`. */
  reportClick(tag: string | undefined): void;
}

export interface NotificationToastProps {
  /** Channel client used to receive broadcast capability events (§5.15). */
  client: ChannelClient;
  /**
   * Notified with the app-level unread total so a host can mirror it in-app
   * (e.g. in the status bar). The **OS** badge is not set here — see below.
   */
  onBadgeChange?: (count: number) => void;
  /**
   * In-app notification delivery (见 23); when absent this component still does
   * its two original jobs and no toast ever arrives from the router.
   */
  inAppNotifications?: InAppNotificationBridge;
}

/**
 * In-app notification sink (架构方案 §5.15). A headless listener mounted
 * persistently by the Workbench, with three jobs.
 *
 * **1. Module toasts** (`NOTIFICATION_EVENT`, from `capabilities.notify`) become
 * shared `toast()`s, rendered by the workbench-mounted `Toaster`. The events are
 * produced by the host-side `context.capabilities` facade only after the calling
 * module passed permission authorization (§5.6).
 *
 * **2. Routed notifications** (见 23), from `capabilities.notifyUser` — the same
 * notification that would have been an OS notification had the user been
 * elsewhere. Unlike (1) these are **not** an EventBus broadcast: the router chose
 * one surface and one window, because the same message toasted in three windows
 * is the same message three times. They carry a click destination, and clicking
 * hands the tag back to the router rather than navigating from here. A
 * `kind: 'sound'` on the same channel plays the bundled chime; the OS toast
 * stays silent so the two do not ring together (见 23 声音).
 *
 * **3. The unread total** (`ATTENTION_CHANGED_EVENT`, 见 23) for the in-app
 * mirror — and **only** the in-app mirror. This used to call the Badging API
 * here too, which is wrong for two reasons: the number would be one module's
 * rather than the app's, and every window would race to write the one badge the
 * app has. The OS-facing surfaces (Dock badge, Windows overlay icon, favicon)
 * belong to each runtime's single attention driver.
 */
export function NotificationToast({
  client,
  onBadgeChange,
  inAppNotifications,
}: NotificationToastProps): ReactNode {
  const t = useT();

  useEffect(() => {
    const disposeNotify = client.listen(NOTIFICATION_EVENT, (data) => {
      const { moduleId, message } = data as NotificationMessage;
      toast(message, { description: moduleId });
    });
    const disposeAttention = client.listen(ATTENTION_CHANGED_EVENT, (data) => {
      onBadgeChange?.((data as AppAttention).count);
    });
    return () => {
      disposeNotify();
      disposeAttention();
    };
  }, [client, onBadgeChange]);

  useEffect(() => {
    if (!inAppNotifications) {
      return;
    }
    // `sonner` keys a toast by its id, so the tag doubles as the dismissal handle
    // and as the coalescing key — a second notification for the same chat
    // replaces the first, exactly as the OS surface does with the same tag.
    return inAppNotifications.subscribe((message) => {
      if (message.kind === 'dismiss') {
        toast.dismiss(message.tag);
        return;
      }
      if (message.kind === 'sound') {
        playNotificationSound();
        return;
      }
      toast(message.title, {
        description: message.body,
        ...(message.tag ? { id: message.tag } : {}),
        action: {
          label: t('notification.open'),
          onClick: () => inAppNotifications.reportClick(message.tag),
        },
      });
    });
  }, [inAppNotifications, t]);

  return null;
}
